// Public surface of the replay module.
//
// Consumers get a `ReplayManager` attached to the `LayersClient` instance
// (see ../index.ts). They can also import these helpers directly to build
// custom integrations, but the wired-in path is the supported one.
import { Recorder, type RecorderOptions, type RrwebProvider } from './recorder.js';
import { ReplayTransport } from './transport.js';
import type { ReplayConfig, ResolvedReplayConfig } from './types.js';

export type {
  ReplayConfig,
  ResolvedReplayConfig,
  SnapshotChunk,
  RrwebRecordFn,
  RrwebRecordOptions,
  RrwebStopFn
} from './types.js';
export { Recorder } from './recorder.js';
export { ReplayTransport } from './transport.js';
export type { RrwebProvider } from './recorder.js';

// Layers-branded selectors, plus the legacy `am-` (AppMachina-era) forms —
// integrations that adopted the old names keep working. The legacy forms
// are deprecated; docs reference only the `layers-` ones.
const DEFAULT_BLOCK_SELECTOR =
  '.layers-no-record, [data-layers-no-record], .am-no-record, [data-am-no-record]';
const DEFAULT_MASK_TEXT_SELECTOR =
  '.layers-mask-text, [data-layers-mask], .am-mask-text, [data-am-mask]';

/**
 * Resolve the user-supplied replay config against remote config + defaults.
 * Pure function — exported so the host SDK can re-resolve when remote config
 * arrives.
 */
export function resolveReplayConfig(
  user: ReplayConfig | undefined,
  remote: { sample_rate?: number; sample_rate_override?: number } | undefined,
  baseUrl: string
): ResolvedReplayConfig {
  // Sample-rate precedence: user override > remote `sample_rate_override`
  // > remote `sample_rate` > 0.
  let sampleRate = 0;
  if (typeof remote?.sample_rate === 'number') sampleRate = clamp01(remote.sample_rate);
  if (typeof remote?.sample_rate_override === 'number') {
    sampleRate = clamp01(remote.sample_rate_override);
  }
  if (typeof user?.sampleRate === 'number') sampleRate = clamp01(user.sampleRate);

  return {
    enabled: user?.enabled ?? false,
    sampleRate,
    maskAllInputs: user?.maskAllInputs ?? true,
    maskAllText: user?.maskAllText ?? false,
    maskTextSelector: user?.maskTextSelector ?? DEFAULT_MASK_TEXT_SELECTOR,
    blockSelector: user?.blockSelector ?? DEFAULT_BLOCK_SELECTOR,
    recordCanvas: user?.recordCanvas ?? false,
    recordCrossOriginIframes: user?.recordCrossOriginIframes ?? false,
    endpoint: user?.endpoint ?? `${baseUrl.replace(/\/$/, '')}/replays`
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export interface InstallReplayOptions {
  config: ResolvedReplayConfig;
  appId: string;
  sdkVersion: string;
  enableDebug?: boolean;
  getSessionId: () => string;
  getPageviewId?: () => string | undefined;
  isConsentGranted: () => boolean;
  rrwebProvider?: RrwebProvider;
}

/**
 * Build a `ReplayManager` and start recording (subject to sampling/consent).
 * Returns a manager regardless of whether recording actually begins, so the
 * host SDK can expose `replay.start()` etc. unconditionally.
 */
export function installReplay(options: InstallReplayOptions): ReplayManager {
  const recorderOpts: RecorderOptions = {
    config: options.config,
    appId: options.appId,
    sdkVersion: options.sdkVersion,
    ...(options.enableDebug != null && { enableDebug: options.enableDebug }),
    getSessionId: options.getSessionId,
    ...(options.getPageviewId != null && { getPageviewId: options.getPageviewId }),
    isConsentGranted: options.isConsentGranted,
    ...(options.rrwebProvider != null && { rrwebProvider: options.rrwebProvider })
  };
  const recorder = new Recorder(recorderOpts);
  const manager = new ReplayManager(recorder);
  if (options.config.enabled) {
    // Fire-and-forget — resolves once rrweb finishes loading.
    void recorder.start();
  }
  return manager;
}

/**
 * Public façade exposed via `layers.replay`. Wraps `Recorder` with a
 * stable, documented surface.
 */
export class ReplayManager {
  constructor(private readonly recorder: Recorder) {}

  /** Start (or resume) recording. */
  async start(): Promise<void> {
    await this.recorder.start();
  }

  /** Stop recording, flush any pending events, and clear in-flight state. */
  stop(): void {
    this.recorder.stop();
  }

  /** Pause recording without resetting the replay ID. */
  pause(): void {
    this.recorder.pause();
  }

  /** Resume after pause. */
  async resume(): Promise<void> {
    await this.recorder.resume();
  }

  /** Current replay ID, or null when not recording. */
  getReplayId(): string | null {
    return this.recorder.getReplayId();
  }

  /** Whether the recorder is actively producing events right now. */
  isRecording(): boolean {
    return this.recorder.isRecording();
  }

  /**
   * Notify the recorder that consent state may have changed. The host SDK
   * should call this whenever it observes `setConsent()` etc.
   */
  onConsentChange(): void {
    this.recorder.onConsentChange();
  }

  /** Page-hide hook — emits any buffered events via `sendBeacon`. */
  flushOnPageHide(): void {
    this.recorder.flushOnPageHide();
  }

  /** Tear down everything — call from the host SDK's `shutdown()`. */
  destroy(): void {
    this.recorder.destroy();
  }

  /** @internal — exposed for tests. */
  _getRecorder(): Recorder {
    return this.recorder;
  }

  /** @internal — exposed for tests. */
  _getTransport(): ReplayTransport {
    return this.recorder._getTransport();
  }
}
